import _ from 'underscore';
import Backbone from 'backbone';
import Radio from 'backbone.radio';
import $ from 'jquery';
import Api from '@/lib/api/api';
import Moment from 'moment';
import v5_Model from '@/models/v5/model';
import v5_Collection from '@/collections/v5/collection';
import model_util from '@/models/utility';
import Device from '@/models/v5/device';
import Dut from '@/models/v5/dut';
import Duts from '@/collections/v5/duts';
import BandwidthModel from '@/models/v5/bandwidth';
import counts_collection from '@/collections/v5/counts';
import Draft from '@/models/v5/draft';

var apiCh = Radio.channel('api');
var TYPES = {
    development: 'development_devicegroup',
    production: 'production_devicegroup',
    factoryfixture: 'factoryfixture_devicegroup',
    dut: 'dut_devicegroup',
    pre_factoryfixture: 'pre_factoryfixture_devicegroup',
    pre_production: 'pre_production_devicegroup',
    pre_dut: 'pre_dut_devicegroup'
};

var HistoryEntry = Backbone.Model.extend({
    parse: function (attrs) {
        return _.extend({
            id: attrs.timestamp + attrs.event
        }, attrs);
    }
});

function create_history_collection(v5_object) {
    var c = new v5_Collection();
    c.url = function () { return Api.urls.device_group_history({ id: v5_object.id }); };
    c.comparator = function(history_item) {
        var ts = Moment(history_item.get('timestamp'));
        return -Number(ts.format('x'));
    };
    c.parse = function parse(models) {
        models = v5_Collection.prototype.parse(models);
        return models.map(function (model) {
            return HistoryEntry.prototype.parse(model);
        });
    };
    return c;
}


function deployment_belongs_to_devicegroup(deployment, devicegroup) {
    var dep_dg = deployment.rel.get('devicegroup');
    if (!dep_dg) return false;
    return dep_dg.id === devicegroup.id;
}


/**
* Fetch from a source collection and add the results to
* a destination collection, using a parse function
* to extract ids from the results
* @param source_coll
* @param dest_coll
* @param parse Function processes the data from the fetch
*              and returns a list of ids
*/
function fetch_and_add(source_coll, dest_coll, parse, options) {
    options = options || {};
    return source_coll.fetch(options).then(function (data) {
        var ids = parse(data);
        var fetched_items = source_coll.filter(function (device) {
            return _(ids).contains(device.id);
        });
        dest_coll.add(fetched_items);
    });
}

function create_draft(devicegroup) {
    var draft = new Draft({
        id: devicegroup.id,
        agent_code: null,
        device_code: null, 
    });
    draft.listenTo(devicegroup, 'change:id', ()=>{
        draft.set({
            id: devicegroup.id
        });
    });
    
    return draft;
}
/**
 * Create a collection of DUTs (devices under test) for this devicegroup
 *
 * DUTs are a hack right now, so all we get back from the API endpoint
 * is a list of device IDs. We will turn each one into a proper device
 * for purposes of the IDE, but API support for this is thin. These
 * devices will *not* appear in the v5/devices response, so this
 * collection is completely self-contained.
 * @param devicegroup
 */
function create_duts(devicegroup) {
    var c = new (v5_Collection.extend({
        model: Dut,
        _prepareModel: function(attrs, options) {
            if (this._isModel(attrs)) {
                attrs.collection = Duts;
            } else {
                options.collection = Duts;
            }
            return Duts._prepareModel.call(this, attrs, options);
        }
    }));
    c.url = function url() {
        return Api.urls.device_group_duts({ id: devicegroup.id });
    };
    c.parse = function parse(resp) {
        try {
            // The reponse is an array of raw device ids.
            // Here we make them look like Device objects
            return resp.data.map(function(id) {
                return {
                    id: id,
                    type: c.model.type,
                    relationships: {
                        devicegroup: {
                            id: devicegroup.id,
                            type: devicegroup.type
                        }
                    }
                };
            });
        } catch (e) {
            // Ignore TypeErrors whic probably mean that data was null
            if (!e.name === 'TypeError') throw e;
            console.warn('Unexpected value in response: %o', resp);
        }
    };
    // For DUTs, set an attribute which views can use to determine whether
    // the device is a DUT or whether it is a normal device
    function on_add(device) {
        var global_devicegroups = apiCh.request('dut');
        if (device.set) device.set({ is_dut: true });

        if (global_devicegroups) {
            global_devicegroups.add(device);
        }

        device.rel.set('devicegroup', devicegroup);
    }
    function on_remove(device) {
        var global_devicegroups = apiCh.request('dut');
        if (device.set) device.set({ is_dut: false });

        if (global_devicegroups) {
            global_devicegroups.remove(device);
        }
        if (device.rel.get('devicegroup') === devicegroup) {
            device.rel.set('devicegroup', null);
        }
    }
    c.listenTo(c, 'add', on_add);
    c.listenTo(c, 'remove', on_remove);
    return c;
}
/**
 * Simple handlers. Passed to a listener when a model is added to some
 * other collection and you want to also add or remove that model to
 * collection coll.
 * 
 * These appear to be working automatically, but leaving in in case testing
 * proves otherwise.
 */
function on_add(coll, model) { coll.add(model); }
function on_remove(coll, model) { coll.remove(model); }

/**
 * Create a combined set of devices and devices under test.
 * This is meaningful for (pre_)?factoryfixture_devicegroups
 * because the duts come from the production_target. Useful
 * in the context of a development 'workspace', because
 * UI wishes to hide the production_target behind the
 * pre_factoryfixture_devicegroup.
 *
 * For other devicegroups this is effectively just the collection
 * of devices.
 */
function create_duts_n_devices(devicegroup) {
    var c = new Backbone.Collection();

    c.listenTo(devicegroup.devices, 'add', _.partial(on_add, c));
    c.listenTo(devicegroup.devices, 'remove', _.partial(on_remove, c));
    setup_duts_n_devices_fetch(c, devicegroup);
    return c;
}

function create_production_target_and_factory_devices(devicegroup){
    var production_target_and_factory_devices = new Backbone.Collection();

    production_target_and_factory_devices.listenTo(
        devicegroup.devices,
        'add',
        _.partial(on_add, production_target_and_factory_devices));

    production_target_and_factory_devices.listenTo(
        devicegroup.devices,
        'remove',
        _.partial(on_remove, production_target_and_factory_devices));

    production_target_and_factory_devices.listenTo(
        devicegroup.duts,
        'add',
        _.partial(on_add, production_target_and_factory_devices));

    production_target_and_factory_devices.listenTo(
        devicegroup.duts,
        'remove',
        _.partial(on_remove, production_target_and_factory_devices));

    production_target_and_factory_devices.fetch = function () {
        production_target_and_factory_devices.stopListening(
            devicegroup.rel.get('production_target').devices,
            'add'
        );
        production_target_and_factory_devices.stopListening(
            devicegroup.rel.get('production_target').devices,
            'remove'
        );

        production_target_and_factory_devices.listenTo(
            devicegroup.rel.get('production_target').devices,
            'add',
            _.partial(on_add, production_target_and_factory_devices)
        );
        production_target_and_factory_devices.listenTo(
            devicegroup.rel.get('production_target').devices,
            'remove',
            _.partial(on_remove, production_target_and_factory_devices)
        );
        $.when(
            devicegroup.devices.fetch({
                data: {
                    'page[size]': 100
                }
            }),
            devicegroup.rel.get('production_target').devices.fetch({
                data: {
                    'page[size]': 100
                }
            }))
            .done(function(){
                production_target_and_factory_devices.add(devicegroup.duts.models, {merge: true});
                production_target_and_factory_devices.add(devicegroup.rel.get('production_target').devices.models, { merge: true });
                production_target_and_factory_devices.add(devicegroup.devices.models, {merge: true});
            });

    };
    return production_target_and_factory_devices;
}
/**
 * Set up fetch function for a devicegroup's duts_n_devices collection, c
 *
 * Need to do this in a function so we can call it after relationships
 * have been set.
 */
function setup_duts_n_devices_fetch(c, devicegroup) {
    if (!c) return;
    var duts;
    var fetch_duts;
    try {
        /**
            * duts
            *
            * "Devices Under Testæ
            *
            * The api returns 'duts' as part of the factory
            * https://electricimp.com/docs/manufacturing/faqs/
            * 'Do I need to write two factory firmwares, one for the Factory BlinkUp Fixture
            *   and another for the target device being tested and blessed?'
        */
        duts = devicegroup.duts;
        fetch_duts = function (options) {
            return fetch_and_add(
                duts,
                c,
                function (data) { return data.data; },
                options
            );
        };
    } catch (e) {
        fetch_duts = function () {
            return $.Deferred().resolve();
        };
    }

    // Shortcut for fetching both collections
    c.fetch = function fetch(options) {
        return fetch_and_add(
            devicegroup.devices,
            c,
            function (data) { return _.pluck(data.data, 'id'); },
            options
        ).then(fetch_duts(options));
    };
}

// This function guards against undefined values for counts.
// The way to guard against this completely is probably to already
// have values defined (fetched), but this will prevent things from
// blowing up if you don't. Caveat: the number may be completely wrong.
function update_counts(counts, amount) {
    counts.set(
        'devices',
        // devices could be undefined because the counts
        // have not yet been fetched. In this case, this count is
        // probably wrong.
        (counts.get('devices') || 0) + amount
    );
}

/**
 * Update counts for other devicegroups. We call this in the context
 * of a bulk assign, and some devices might already belong to another
 * devicegroup. We need to update the counts on those devicegroups.
 * Assignment between devicegroups in a Product should always result in the
 * net number of devices remaining the same. Without doing this, that
 * number would only increment.
 *
 * @param attempted_ids Array of device ids
 * @param errors an errors response object from API
 */
function update_other_counts(attempted_ids, errors) {
    errors = errors || [];
    var devices = apiCh.request('device');
    function get_id_from_error(error) {
        var id;
        try {
            id = attempted_ids[
                error.source.pointer.match(/[\d+]/)[0]
            ];
        } catch (e) {
            // ignore
        }
        return id;
    }
    function increment_devices_count(device) {
        var devicegroup = device.rel.get('devicegroup');
        if (devicegroup && devicegroup.counts) {
            var devices_count = devicegroup.counts.get('devices');
            devicegroup.counts.set('devices', --devices_count);
        }
    }
    function get_device_from_id(id) {
        return devices.get(id);
    }
    var failed_ids = _.chain(errors)
        .map(get_id_from_error)
        .compact()
        .value();
    // For every successfully assigned device, find its old devicecroup
    // and decrement its devices count
    _.chain(attempted_ids)
        .difference(failed_ids)
        .map(get_device_from_id)
        .compact()
        .each(increment_devices_count);
}

var DeviceGroup = v5_Model.extend({
    initialize: function() {
        var devicegroup = this;
        this.counts = counts_collection.create_counts_model_for(
            this, Api.urls.device_group_counts, Device.type
        );
        this.duts = create_duts(this);
        this.devices = model_util.create_relationship_collection(
            this,
            'device',
            'devicegroup',
            function() {
                return Api.urls.device_group_devices({ id: devicegroup.id });
            },
            {
                'filter[devicegroup.id]': devicegroup.id
            }
        );
        this.webhooks = model_util.create_relationship_collection(
            this,
            'webhook',
            'devicegroup',
            function() {
                return Api.urls.devicegroup_webhooks({ devicegroup_id: devicegroup.id });
            },
            {
                'filter[devicegroup.id]': devicegroup.id
            }
        );
        this.duts_n_devices = create_duts_n_devices(this);
        this.production_target_and_factory_devices = create_production_target_and_factory_devices(this);
        this.draft = create_draft(this);
        this.listenTo(this, 'change', this.update_drafts_product);
        _.bindAll(this, 'restart', 'create_deployment', 'latest_deployment');
        this.bandwidth = new BandwidthModel(this);
        // Necessary for bandwidth to get ID if devicegroup was created without one
        // i.e. if devicegroup was created, and then fetched to get its ID
        this.bandwidth.listenTo(this, 'change:id', ()=>{ this.bandwidth.set('id', this.id); });
        this.history = create_history_collection(this);
    },
    parse: function(resp){
        if (resp.data && resp.data.type) {
            this.type = resp.data.type;
        }
        return Api.parse_item(resp);
    },
    /**
     * Override destroy so we can decrement product's devicegroup count
     */
    destroy: function(options) {
        options = options || {};
        var success = options.success;
        options.success = function(model, resp, options) {
            if (success) success(model, resp, options);
        };
        v5_Model.prototype.destroy.call(this, options);
    },
    update_drafts_product: function() {
        var product = this.rel.get('product');
        if (product) {
            this.draft.rel.set('product', product);
        }
    },
    /**
     * Unassign devices from this devicegroup. Devices will disappear from
     * this model's devices collection on success.
     *
     * @param Array devices, an array of Devices or device_ids
     * @param Object options, like options for $.ajax
     */
    unassign: function(devices, options) {
        options = options || {};
        var counts = this.counts;
        var devicegroup = this;
        devices = _.compact((devices && devices.map(function(item){
            var id;
            if (typeof item == 'string') {
                id = parseInt(item, 10);
            } else if (item.id) {
                id = item.id;
            } else {
                return console.warn("Unknown device %o", item);
            }
            return {
                id: id,
                type: Device.type
            };
        })) || []);
        var success = options.success;
        var error = options.error;
        options.success = function() {
            update_counts(counts, -devices.length);
            devicegroup.devices.remove(devices);
            if (success) success.apply(options.context, arguments);
        };
        options.error = function(resp){
            // Try to read the response meta info and throw away
            // any errors related to walking through that expected
            // object hierarchy
            try {
                update_counts(counts, -resp.responseJSON.meta.succeeded);
            } catch (e) {
                if (e.name !== 'TypeError') throw e;
            }
            if (error) error.apply(options.context, arguments);
        };
        // TODO: partial success
        options = _.extend({
            type: 'DELETE',
            url: Api.urls.device_group_devices({ id: this.id }),
            data: JSON.stringify({ data: devices }),
        }, options);
        return Api.ajax_v5(options);
    },
    unassign_all: function() {
        var promise = new $.Deferred();
        function success() {
            return promise.resolve();
        }
        function error(reasons) {
            return promise.reject(reasons);
        }
        this.unassign(this.devices.models, {
            success: success,
            error: error
        });
        return promise;
    },
    
    /**
     * Custom set relationships_because we need to set up listeners
     * for the production_target, which could be set after this model
     * is initialized.
     *
     * TODO: revamp model relationships
     */
    set_relationships: function() {
        var result = v5_Model.prototype.set_relationships.apply(this, arguments);
        setup_duts_n_devices_fetch(this.duts_n_devices, this);

        return result;
    },
    /**
     * Assign devices to the devicegroup's relationships
     * @param device_ids Array
     */
    bulk_assign_devices: function(device_ids, options) {
        options = options || {};
        var counts = this.counts;
        var devices = device_ids.map(function(id){
            return { type: Device.type, id: id };
        });
        var data = JSON.stringify({
            data: devices
        });

        var error = options.error;
        options.error = function(resp) {
            var r = resp.responseJSON;
            var succeeded = (r && r.meta && r.meta.succeeded) || 0;
            update_counts(counts, succeeded);
            update_other_counts(device_ids, resp && resp.responseJSON && resp.responseJSON.errors);
            if (error) error.apply(options.context, arguments);
        };

        var success = options.success;
        options.success = function() {
            update_counts(counts, devices.length);
            update_other_counts(device_ids);
            if (success) success.apply(options.context, arguments);
        };

        options = _.extend({
            url: Api.urls.device_group_devices({ id: this.id }),
            data: data
        }, options);
        return this.sync('create', this, options);
    },
    /**
     * Sends an event to online devices that a new deployment is available.
     * Devices with handlers will decide whether or not to restart to get 
     * the new version
     */
    conditional_restart: function(options) {
        options = _.extend({
            type: 'POST',
            url: Api.urls.device_group_conditional_restart({ id: this.id })
        }, options);
        return Api.ajax_v5(options);
    },
    /**
     * Forces devices on this devicegroup to restart
     */
    restart: function(options) {
        options = _.extend({
            type: 'POST',
            url: Api.urls.device_group_restart({ id: this.id }),
        }, options);
        return Api.ajax_v5(options);
    },
    create_deployment: function(code_model) {
        var deployments = apiCh.request('deployment');
        var deployment = {
            attributes: {
                agent_code: code_model.get('agent_code') || '',
                device_code: code_model.get('device_code') || ''
            },
            relationships: {
                devicegroup: {
                    id: this.id,
                    type: this.type
                }
            },
            type: 'deployment'
        };
        return new Promise(function(resolve, reject) {
            deployments.create(deployment, {
                wait: true,
                success: resolve,
                error: function(model, response) { reject(response); },
                url: Api.urls.deployments_nocode({ nocode: true }),
            });
        });
    },
    /**
     * Get the latest deployment for a devicegroup.
     * Warning; this is not a fetch! You must have already fetched deployments
     * for this devicegroup
     */
    latest_deployment: function() {
        var deployments = apiCh.request('deployment').filter(
            _.partial(deployment_belongs_to_devicegroup, _, this)
        );
        var sorted = _.sortBy(deployments, function(deployment) {
            return deployment.get('created_at');
        });
        return _.last(sorted);
    },
    /**
     * Sets the minimum deployment for a devicegroup
     * @param deployment a Deployment model
     * @return jQuery.deferred
     */
    set_minimum_deployment: function(deployment) {
        this.rel.set('min_supported_deployment', deployment);
        return Api.ajax_v5({
            type: 'put',
            data: JSON.stringify({
                data: deployment.toReference()
            }),
            url: Api.urls.device_group_min_deployment({ id: this.id }),
        });
    },
    // Update draft against whatever's on the server
    // Server might return a 404 if there's no draft
    // in that case, allow a .then off an empty draft
    fetch_draft: function(){
        return this.draft.fetch();
    },
    requires_mfa: function(){
        let owner = this.rel.get('owner');
        if ( this.type.indexOf('development') > -1
            || this.type.indexOf('pre') > -1
            || this.type.indexOf('factory') > -1 ) {
            return false;
        } else if ( owner.get('deployment_mfa_required') ){
            return true;
        }
        return false;
    },
    /**
     * Get a human readable name from a devicegroup's type
     *
     * Currently handles development_devicegroup,
     * factoryfixture_devicegroup, pre_factoryfixture_devicegroup,
     * pre_production_devicegroup, production_devicegroup
     *
     * For an internationalized world, or, when
     */
    type_readable: function() {

        var readable = {
            "development_devicegroup"        : "Development Device Group",
            "factoryfixture_devicegroup"     : "Fixture Device Group",
            "pre_factoryfixture_devicegroup" : "Test Fixture Device Group",
            "pre_production_devicegroup"     : "Test Production Device Group",
            "production_devicegroup"         : "Production Device Group",
            "pre_dut_devicegroup"            : "Test DUT Device Group",
            "dut_devicegroup"                : "DUT Device Group"
        };

        return readable[this.type];
    },
    type_abbreviated: function() {

        var readable = {
            "development_devicegroup"        : "Device Group",
            "factoryfixture_devicegroup"     : "Fixture",
            "pre_factoryfixture_devicegroup" : "Device Group",
            "pre_production_devicegroup"     : "Device Group",
            "production_devicegroup"         : "Device Group",
            "pre_dut_devicegroup"            : "Test DUT",
            "dut_devicegroup"                : "DUT"
        };

        return readable[this.type];
    },
    TYPES_MAP: TYPES,
    types: _.values(TYPES),

});
export default DeviceGroup;

